import {
  buildShowCompletedGoalsUpdate,
  getShowCompletedGoals,
  GoalCompletedContext,
  isGoalArchived,
} from '@griply/utils';
import { useGoalById, useGoals } from 'features/goals';
import { useUpdateUserSettings } from 'features/user/hooks/use-update-user-settings';
import { OutletContext } from 'pages/goal-detail/outlet-context';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext, useParams } from 'react-router';
import { Plus, SettingsSort18 } from 'shared/assets/icons';
import { Colors, IconButton } from 'shared/components/ui/icon-button';
import { PageSectionBase } from 'shared/components/ui/page-section-base';
import {
  PopupMenu,
  PopupMenuList,
  PopupMenuListItem,
  PopupMenuToggle,
} from 'shared/components/ui/popup-menu';
import { useEntityLinks } from 'shared/hooks/use-entity-links';
import { useUser } from 'shared/hooks/use-user';
import { Paths } from 'shared/routes/paths';
import { replaceUrlParams } from 'shared/utils/replace-url-params';

import { GoalDetailRoadmapAddMenu } from './components/goal-detail-roadmap-add-menu';
import { SubgoalsFilterButton } from './components/subgoals-filter-button';
import { SubgoalsOverview } from './components/subgoals-overview';
import * as Styled from './goal-detail.style';

export const SummaryRoadmap: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { mainScrollRef } = useOutletContext<OutletContext>();
  const goal = useGoalById(id);
  const goals = useGoals();
  const links = useEntityLinks();
  const user = useUser();
  const updateUserSettings = useUpdateUserSettings();
  const interactionsRef = useRef<HTMLDivElement>(null);
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);

  // An archived goal's sub-goals are archived too, so hiding them empties the list.
  const isArchived = useMemo(
    () => !!goal && isGoalArchived(goal, goals, { links }),
    [goal, goals, links],
  );

  const showCompletedGoals = getShowCompletedGoals(
    user?.settings,
    GoalCompletedContext.GoalDetail,
  );

  const onShowCompletedGoals = (value: boolean) => {
    updateUserSettings(
      buildShowCompletedGoalsUpdate(GoalCompletedContext.GoalDetail, value),
    );
  };

  const goalsUrl = useMemo(
    () => replaceUrlParams(Paths.GoalDetailRoadmap, { id: id! }),
    [id],
  );

  if (!goal) {
    return null;
  }

  const handleOpenAddMenu = () => setIsAddMenuOpen(true);
  const handleCloseAddMenu = () => setIsAddMenuOpen(false);
  const handleOpenSortMenu = () => setIsSortMenuOpen(true);
  const handleCloseSortMenu = () => setIsSortMenuOpen(false);

  return (
    <PageSectionBase
      title={t('pages.goal-detail.tabs.roadmap.label')}
      url={goalsUrl}
      interactions={
        <Styled.InteractionsContainer ref={interactionsRef}>
          <IconButton
            icon={Plus}
            ref={addButtonRef}
            onClick={handleOpenAddMenu}
            color={Colors.Default}
          />
          <SubgoalsFilterButton goal={goal} />
          <IconButton
            icon={SettingsSort18}
            onClick={handleOpenSortMenu}
            color={Colors.Default}
          />
          {isAddMenuOpen && (
            <PopupMenu
              referenceElement={addButtonRef}
              onOutside={handleCloseAddMenu}
              position="bottom-end"
            >
              <GoalDetailRoadmapAddMenu
                parentIds={[goal.id]}
                onClose={handleCloseAddMenu}
              />
            </PopupMenu>
          )}
          {isSortMenuOpen && (
            <PopupMenu
              referenceElement={interactionsRef}
              onOutside={handleCloseSortMenu}
              position="bottom-end"
            >
              <PopupMenuList>
                <PopupMenuListItem>
                  <PopupMenuToggle
                    label={t('general.labels.goals.show-completed-goals')}
                    checked={showCompletedGoals}
                    onChange={onShowCompletedGoals}
                  />
                </PopupMenuListItem>
              </PopupMenuList>
            </PopupMenu>
          )}
        </Styled.InteractionsContainer>
      }
      noPadding
    >
      <SubgoalsOverview
        goal={goal}
        hideCompleted={!showCompletedGoals}
        scrollElement={mainScrollRef}
        hideArchived={!isArchived}
      />
    </PageSectionBase>
  );
};
